import React from 'react';
import { Input } from '../../BasicComponents/Input/Input';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { currencySymbol, formatMoneyParts } from '../../../utils/currency';
import type { DenominationEntryProps } from './types';

// ─── DenominationEntry ────────────────────────────────────────────────────────

export function DenominationEntry({
  rows,
  onChange,
  totalAmount,
  currency = 'INR',
  className,
}: DenominationEntryProps) {
  const symbol = currencySymbol(currency);
  // Minor units per major unit for this tenant (INR 100, OMR/BHD/KWD 1000).
  const subunit = getSubunitMultiplier(currency);
  // Fixed decimals, no grouping. The decimal count comes from the currency, so
  // a 3-decimal tenant gets OMR1.500 where a hardcoded 2 gave OMR1.50.
  // Byte-identical for INR (100 -> 2 decimals).
  const decimals = getCurrencyDecimals(currency);
  const formatCurrency = (paise: number): string =>
    formatMoneyParts(paise, currency, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: false,
    });
  // Rounded per row: a fractional GCC face (0.005 rial) times its subunit is not
  // exactly representable as a float.
  const toMinor = (denomination: number): number => Math.round(denomination * subunit);
  const actualTotal = rows.reduce((sum, r) => sum + toMinor(r.denomination) * r.count, 0);
  const variance = totalAmount !== undefined ? actualTotal - totalAmount : null;

  function handleCountChange(denomination: number, raw: string) {
    const count = parseInt(raw, 10);
    const updated = rows.map((r) =>
      r.denomination === denomination ? { ...r, count: isNaN(count) ? 0 : Math.max(0, count) } : r,
    );
    onChange(updated);
  }

  return (
    <div className={`flex flex-col gap-3 ${className ?? ''}`}>
      <table role="table" className="w-full text-sm">
        <thead>
          <tr className="text-neutral-500 dark:text-neutral-400 text-xs uppercase tracking-wide">
            <th className="text-left pb-2 font-medium">Denomination</th>
            <th className="text-center pb-2 font-medium">Count</th>
            <th className="text-right pb-2 font-medium">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const lineTotal = toMinor(row.denomination) * row.count;
            return (
              <tr
                key={row.denomination}
                role="row"
                className="border-t border-neutral-100 dark:border-neutral-700"
              >
                <td className="py-1.5 text-neutral-700 dark:text-neutral-200 font-medium">
                  {symbol}{row.denomination}
                </td>
                <td className="py-1.5 px-2">
                  <Input
                    value={row.count === 0 ? '' : String(row.count)}
                    onChange={(value) => handleCountChange(row.denomination, value)}
                    inputMode="numeric"
                    placeholder="0"
                    size="sm"
                    aria-label={`Count of ${symbol}${row.denomination} notes`}
                    className="text-center"
                  />
                </td>
                <td
                  className={`py-1.5 text-right tabular-nums ${
                    lineTotal > 0
                      ? 'text-neutral-700 dark:text-neutral-200'
                      : 'text-neutral-400 dark:text-neutral-500'
                  }`}
                >
                  {formatMoneyParts(lineTotal, currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div className="border-t border-neutral-200 dark:border-neutral-600 pt-3 space-y-1.5">
        <div className="flex justify-between text-sm font-semibold text-neutral-800 dark:text-neutral-100">
          <span>Actual Cash</span>
          <span aria-live="polite">{formatCurrency(actualTotal)}</span>
        </div>

        {totalAmount !== undefined && (
          <>
            <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
              <span>Expected Cash</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>
            {variance !== null && (
              <div
                aria-live="polite"
                className={[
                  'flex justify-between text-sm font-semibold rounded px-2 py-1',
                  variance === 0
                    ? 'bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400'
                    : variance > 0
                    ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
                    : 'bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span>Variance</span>
                <span>
                  {variance === 0
                    ? `${symbol}0 (No variance)`
                    : `${variance > 0 ? '+' : ''}${formatCurrency(variance)}`}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
